import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Plus, Minus, Search, Package, Shirt, Sofa, FileText, Hash, MessageSquare, Calendar, Printer, Truck, MapPin, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { DepositSavedModal, buildDepositSms } from "@/components/admin/DepositSavedModal";

const API = process.env.REACT_APP_BACKEND_URL;

// Articles list based on the tariff grid - Complete list from pressing-sakura.fr/tarifs
const ARTICLES = {
  vetements: {
    label: "Vêtements",
    icon: Shirt,
    items: [
      { id: "jupe", name: "Jupe" },
      { id: "pantalon", name: "Pantalon" },
      { id: "veste", name: "Veste" },
      { id: "gilet", name: "Gilet" },
      { id: "cravate_foulard", name: "Cravate, foulard, écharpe" },
      { id: "short_bermuda", name: "Short, bermuda" },
      { id: "vetement_bebe", name: "Vêtement de bébé" },
      { id: "blouse", name: "Blouse" },
      { id: "manteau", name: "Manteau" },
      { id: "manteau_fourrure", name: "Manteau fourrure acrylique" },
      { id: "veste_fourrure", name: "Veste fourrure acrylique" },
      { id: "trench", name: "Trench, imperméable" },
      { id: "combinaison_ski", name: "Combinaison de ski" },
      { id: "pull", name: "Pull" },
      { id: "chemise", name: "Chemise, polo, t-shirt" },
      { id: "doudoune", name: "Doudoune" },
      { id: "blouson", name: "Blouson" },
      { id: "robe_simple", name: "Robe simple" },
      { id: "robe_autre", name: "Robe autre" },
      { id: "robe_mariee", name: "Robe de mariée" },
    ]
  },
  linge_panier: {
    label: "Linge au panier",
    icon: Package,
    items: [
      { id: "panier_lave_repasse_plie", name: "Linge au panier, lavé, repassé, plié" },
      { id: "panier_repasse_plie", name: "Linge au panier, repassé, plié" },
      { id: "panier_lave_plie", name: "Linge au panier, lavé, plié" },
    ]
  },
  repassage: {
    label: "Repassage uniquement",
    icon: Package,
    items: [
      { id: "repassage_chemise", name: "Chemise, polo, t-shirt (repassage)" },
      { id: "repassage_pantalon", name: "Pantalon, veste (repassage)" },
      { id: "repassage_drap", name: "Drap, drap housse (repassage)" },
      { id: "repassage_housse_couette", name: "Housse de couette (repassage)" },
    ]
  },
  literie: {
    label: "Literie et ameublement",
    icon: Sofa,
    items: [
      { id: "couette", name: "Couette" },
      { id: "couverture", name: "Couverture, dessus de lit" },
      { id: "sac_couchage", name: "Sac de couchage" },
      { id: "couette_plume", name: "Couette en plume" },
      { id: "housse_canape", name: "Housse de canapé" },
      { id: "rideau", name: "Rideau" },
      { id: "plaid", name: "Plaid" },
      { id: "housse_coussin_petit", name: "Housse de coussin (petit)" },
      { id: "housse_coussin_grand", name: "Housse de coussin (grand)" },
      { id: "taie", name: "Taie, traversin" },
      { id: "drap", name: "Drap, drap housse" },
      { id: "housse_couette", name: "Housse de couette" },
      { id: "nappe", name: "Nappe" },
      { id: "serviette_table", name: "Serviette de table" },
      { id: "serviette_eponge", name: "Serviette éponge" },
      { id: "oreiller", name: "Oreiller, coussin" },
      { id: "oreiller_plume", name: "Oreiller en plume" },
      { id: "tapis", name: "Tapis" },
    ]
  }
};

const ArticleItem = ({ item, quantity, onQuantityChange }) => {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
      quantity > 0 ? "bg-pink-50 border-pink-300" : "bg-white border-slate-200"
    }`}>
      <span className={`font-medium ${quantity > 0 ? "text-pink-700" : "text-slate-700"}`}>
        {item.name}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onQuantityChange(item.id, Math.max(0, quantity - 1))}
          disabled={quantity === 0}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <span className={`w-8 text-center font-bold ${quantity > 0 ? "text-pink-600" : "text-slate-400"}`}>
          {quantity}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 hover:bg-pink-100 hover:border-pink-300"
          onClick={() => onQuantityChange(item.id, quantity + 1)}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const CategorySection = ({ category, quantities, onQuantityChange, searchTerm }) => {
  const Icon = category.icon;
  const filteredItems = category.items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (filteredItems.length === 0) return null;
  
  const categoryTotal = filteredItems.reduce((sum, item) => sum + (quantities[item.id] || 0), 0);
  
  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-pink-500" />
            {category.label}
          </div>
          {categoryTotal > 0 && (
            <Badge className="bg-pink-500">{categoryTotal} article(s)</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {filteredItems.map(item => (
          <ArticleItem
            key={item.id}
            item={item}
            quantity={quantities[item.id] || 0}
            onQuantityChange={onQuantityChange}
          />
        ))}
      </CardContent>
    </Card>
  );
};

export default function DepositSlipPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [upcomingBooking, setUpcomingBooking] = useState(null);
  
  // Form state
  const [quantities, setQuantities] = useState({});
  const [ticketNumber, setTicketNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [estimatedDate, setEstimatedDate] = useState("");
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printAmount, setPrintAmount] = useState("");
  const [savedDeposit, setSavedDeposit] = useState(null);
  
  // Delivery slot booking modal state
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingSlot, setBookingSlot] = useState(false);
  
  const getAuth = () => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  };
  
  useEffect(() => {
    fetchCustomerAndBookings();
  }, [customerId]);
  
  const fetchCustomerAndBookings = async () => {
    const headers = getAuth();
    if (!headers) return;
    
    try {
      // Fetch customer info
      const customerRes = await axios.get(`${API}/api/admin/customers/${customerId}`, { headers });
      setCustomer(customerRes.data);
      
      // Fetch customer's bookings to find upcoming delivery date
      try {
        const bookingsRes = await axios.get(`${API}/api/admin/bookings`, { headers });
        const customerBookings = bookingsRes.data.filter(b => 
          b.email === customerRes.data.email && 
          (b.status === "pending" || b.status === "confirmed") &&
          b.delivery_date
        );
        
        // Find the nearest upcoming delivery
        if (customerBookings.length > 0) {
          // Sort by delivery date ascending
          customerBookings.sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));
          const nextBooking = customerBookings[0];
          setUpcomingBooking(nextBooking);
          
          // Pre-fill the estimated date with the delivery date
          if (nextBooking.delivery_date) {
            setEstimatedDate(nextBooking.delivery_date);
          }
        }
      } catch (bookingError) {
        console.error("Error fetching bookings:", bookingError);
        // Non-blocking - continue without pre-filled date
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du client");
      navigate("/admin/customers");
    } finally {
      setLoading(false);
    }
  };
  
  const handleQuantityChange = (itemId, quantity) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  };
  
  // Fetch available delivery slots
  const fetchAvailableSlots = async (zoneId) => {
    const headers = getAuth();
    if (!headers) return;
    
    setLoadingSlots(true);
    setSelectedZone(zoneId);
    
    try {
      // Get all slots for the zone (API returns future slots)
      const response = await axios.get(`${API}/api/slots/${zoneId}`);
      
      // Filter slots that have availability and limit to next 30 days
      const today = new Date();
      const maxDate = addDays(today, 30);
      
      const slotsWithAvailability = (response.data || []).filter(slot => {
        const slotDate = new Date(slot.date);
        return slot.current_bookings < slot.max_bookings && 
               slot.is_active && 
               slotDate <= maxDate;
      });
      
      // Sort by date
      slotsWithAvailability.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setAvailableSlots(slotsWithAvailability);
      
      if (slotsWithAvailability.length === 0) {
        toast.info("Aucun créneau disponible dans les 30 prochains jours");
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Erreur lors du chargement des créneaux");
    } finally {
      setLoadingSlots(false);
    }
  };
  
  // Book a delivery slot for the customer
  const bookDeliverySlot = async (slot) => {
    if (!customer) return;
    
    const headers = getAuth();
    if (!headers) return;
    
    setBookingSlot(true);
    
    try {
      // Create a delivery-only booking
      const bookingData = {
        zone_id: selectedZone,
        delivery_slot_id: slot.id,
        pickup_slot_id: null,
        booking_type: "delivery",
        first_name: customer.first_name,
        last_name: customer.last_name,
        address: customer.address,
        phone: customer.phone || "",
        email: customer.email,
        remarks: `Livraison créée depuis le dépôt - Ticket #${ticketNumber || "N/A"}`
      };
      
      await axios.post(`${API}/api/bookings`, bookingData);
      
      // Update the estimated date on the deposit slip
      setEstimatedDate(slot.date);
      setUpcomingBooking({ delivery_date: slot.date, delivery_time: `${slot.start_time}-${slot.end_time}` });
      
      toast.success(`Livraison réservée pour le ${format(new Date(slot.date), "EEEE d MMMM", { locale: fr })}`);
      setShowSlotModal(false);
    } catch (error) {
      console.error("Error booking slot:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la réservation");
    } finally {
      setBookingSlot(false);
    }
  };
  
  const getTotalArticles = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };
  
  const getSelectedArticles = () => {
    const selected = [];
    Object.entries(ARTICLES).forEach(([categoryKey, category]) => {
      category.items.forEach(item => {
        if (quantities[item.id] > 0) {
          selected.push({
            name: item.name,
            quantity: quantities[item.id],
            category: category.label
          });
        }
      });
    });
    return selected;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer?.email) {
      toast.error("Ce client n'a pas d'adresse email");
      return;
    }
    await saveDeposit(true);
  };

  const saveDeposit = async (sendEmail) => {
    const selectedArticles = getSelectedArticles();
    if (selectedArticles.length === 0) {
      toast.error("Veuillez sélectionner au moins un article");
      return;
    }
    
    setSending(true);
    try {
      const headers = getAuth();
      if (!headers) return;
      
      const res = await axios.post(`${API}/api/admin/deposit-slip`, {
        customer_id: customerId,
        customer_name: `${customer.first_name} ${customer.last_name}`,
        customer_email: customer.email || null,
        send_email: sendEmail,
        ticket_number: ticketNumber,
        articles: selectedArticles,
        remarks: remarks,
        estimated_date: estimatedDate || null,
        booking_id: upcomingBooking?.id || null,
        additional_treatments: upcomingBooking?.additional_treatments || null
      }, { headers });
      
      if (!sendEmail) {
        setSavedDeposit(res.data);
        return;
      }
      toast.success("Bon de dépôt envoyé par email !");
      navigate("/admin/customers");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du bon de dépôt");
    } finally {
      setSending(false);
    }
  };
  
  const handlePrintClick = () => {
    const selectedArticles = getSelectedArticles();
    if (selectedArticles.length === 0) {
      toast.error("Veuillez sélectionner au moins un article");
      return;
    }
    setShowPrintModal(true);
  };
  
  const handlePrint = async () => {
    const printWindow = window.open('', '_blank');
    let prepaidShirts = customer?.prepaid_shirts || 0;
    try {
      const headers = getAuth();
      if (headers) {
        const fresh = await axios.get(`${API}/api/admin/customers/${customerId}`, { headers });
        prepaidShirts = fresh.data.prepaid_shirts || 0;
        setCustomer(fresh.data);
      }
    } catch (error) {
      // keep last known balance
    }
    const selectedArticles = getSelectedArticles();
    
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    
    // Format estimated date
    let estimatedDateStr = "";
    if (estimatedDate) {
      const estDate = new Date(estimatedDate);
      estimatedDateStr = estDate.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    }
    
    // Group articles by category
    const articlesByCategory = {};
    selectedArticles.forEach(article => {
      if (!articlesByCategory[article.category]) {
        articlesByCategory[article.category] = [];
      }
      articlesByCategory[article.category].push(article);
    });
    
    // Build articles HTML
    let articlesHtml = '';
    Object.entries(articlesByCategory).forEach(([category, items]) => {
      articlesHtml += `<div style="margin: 6px 0; border-top: 2px dashed #000; padding-top: 5px; color: #000;">
        <div style="font-weight: 900; font-size: 12px; text-transform: uppercase; margin-bottom: 4px; color: #000;">${category}</div>`;
      items.forEach(item => {
        articlesHtml += `<div style="display: flex; justify-content: space-between; font-size: 13px; padding: 3px 0; color: #000; font-weight: bold;">
          <span style="color: #000; font-weight: bold;">${item.name}</span>
          <span style="font-weight: 900; color: #000;">x${item.quantity}</span>
        </div>`;
      });
      articlesHtml += '</div>';
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Ticket - ${customer?.first_name} ${customer?.last_name}</title>
        <style>
          @page { 
            size: 80mm auto; 
            margin: 0; 
          }
          * {
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: 'Courier New', Courier, monospace;
            width: 80mm;
            margin: 0 auto;
            padding: 5mm;
            font-size: 13px;
            line-height: 1.3;
            color: #000;
            background: #fff;
            font-weight: bold;
          }
          .logo {
            text-align: center;
            margin-bottom: 8px;
          }
          .logo img {
            max-width: 50mm;
            height: auto;
            filter: grayscale(100%) contrast(200%) brightness(0.9);
          }
          .ticket-number {
            text-align: center;
            font-size: 22px;
            font-weight: 900;
            border: 3px solid #000;
            padding: 8px;
            margin: 8px 0;
            color: #000;
            background: #fff;
          }
          .client {
            text-align: center;
            font-size: 15px;
            font-weight: 900;
            margin: 8px 0;
            padding: 5px;
            border: 3px solid #000;
            color: #000;
            background: #fff;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            margin: 4px 0;
            color: #000;
            font-weight: bold;
          }
          .separator {
            border: none;
            border-top: 3px dashed #000;
            margin: 8px 0;
          }
          .total {
            text-align: center;
            font-size: 15px;
            font-weight: 900;
            margin: 8px 0;
            padding: 6px;
            border: 3px solid #000;
            color: #000;
            background: #fff;
          }
          .amount {
            text-align: center;
            font-size: 20px;
            font-weight: 900;
            margin: 8px 0;
            padding: 10px;
            border: 4px solid #000;
            color: #000;
            background: #fff;
          }
          .remarks {
            margin: 8px 0;
            padding: 8px;
            font-size: 12px;
            border: 2px solid #000;
            color: #000;
            background: #fff;
            font-weight: bold;
          }
          .estimated-date {
            text-align: center;
            margin: 8px 0;
            padding: 8px;
            border: 2px dashed #000;
            font-size: 13px;
            color: #000;
            background: #fff;
            font-weight: bold;
          }
          .footer {
            text-align: center;
            font-size: 11px;
            margin-top: 12px;
            color: #000;
            font-weight: bold;
          }
          span, div, p, strong {
            color: #000 !important;
            font-weight: bold !important;
          }
          @media print {
            * {
              color: #000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body { 
              width: 80mm; 
              margin: 0;
              padding: 3mm;
              color: #000;
              background: #fff;
              font-weight: bold;
            }
            .logo img {
              filter: grayscale(100%) contrast(200%) brightness(0.9);
            }
            span, div, p, strong {
              color: #000 !important;
              font-weight: bold !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="logo">
          <img src="/logo-sakura.png" alt="Pressing Sakura" />
        </div>
        
        ${ticketNumber ? `<div class="ticket-number">N° ${ticketNumber}</div>` : ''}
        
        <div class="client">${customer?.first_name} ${customer?.last_name}</div>
        
        <div class="info-row">
          <span>Date dépôt:</span>
          <span>${dateStr} ${timeStr}</span>
        </div>
        
        <div class="separator"></div>
        
        <div style="font-weight: 900; text-align: center; margin-bottom: 5px; color: #000; font-size: 13px;">ARTICLES DÉPOSÉS</div>
        ${articlesHtml}
        
        <div class="separator"></div>
        
        <div class="total">${getTotalArticles()} ARTICLE(S)</div>
        
        ${printAmount ? `<div class="amount">TOTAL : ${printAmount} €</div>` : ''}
        
        ${prepaidShirts > 0 ? `<div class="amount" style="font-size:16px;">CHEMISES RESTANTES : ${prepaidShirts}</div>` : ''}
        
        ${remarks ? `
          <div class="remarks">
            <strong>REMARQUES:</strong><br>
            ${remarks}
          </div>
        ` : ''}
        
        ${estimatedDateStr ? `
          <div class="estimated-date">
            <strong>RETRAIT PRÉVU</strong><br>
            ${estimatedDateStr}
          </div>
        ` : ''}
        
        <div class="footer">
          Merci de votre confiance !<br>
          Pressing Sakura
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setShowPrintModal(false);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin/customers")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Bon de dépôt</h1>
                {customer && (
                  <p className="text-sm text-slate-500">
                    {customer.first_name} {customer.last_name}
                  </p>
                )}
              </div>
            </div>
            {getTotalArticles() > 0 && (
              <Badge className="bg-pink-500 text-lg px-3 py-1">
                {getTotalArticles()} article(s)
              </Badge>
            )}
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit}>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 text-lg"
              />
            </div>
          </div>
          
          {/* Ticket Number & Estimated Date */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Hash className="w-4 h-4 text-pink-500" />
                    N° Ticket de marquage
                  </Label>
                  <Input
                    type="text"
                    value={ticketNumber}
                    onChange={(e) => setTicketNumber(e.target.value)}
                    placeholder="Ex: 12345"
                    className="h-12 text-lg"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-pink-500" />
                    Date de retrait estimée
                  </Label>
                  <Input
                    type="date"
                    value={estimatedDate}
                    onChange={(e) => setEstimatedDate(e.target.value)}
                    className="h-12 text-lg"
                  />
                  {upcomingBooking && estimatedDate === upcomingBooking.delivery_date && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      ✓ Pré-remplie depuis la réservation du client
                      {upcomingBooking.delivery_time && ` (${upcomingBooking.delivery_time})`}
                    </p>
                  )}
                  
                  {/* Button to book delivery slot if no date */}
                  {!upcomingBooking && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => setShowSlotModal(true)}
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Réserver un créneau de livraison
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Delivery Slot Modal */}
          {showSlotModal && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
              <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-500" />
                    Réserver un créneau de livraison
                  </CardTitle>
                  <p className="text-sm text-slate-500">Pour {customer?.first_name} {customer?.last_name}</p>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Zone selection */}
                  {!selectedZone && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-700">Choisissez la zone :</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          variant="outline"
                          className="h-16 flex-col gap-1"
                          onClick={() => fetchAvailableSlots("lyon")}
                        >
                          <MapPin className="w-5 h-5 text-pink-500" />
                          <span>Lyon</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-16 flex-col gap-1"
                          onClick={() => fetchAvailableSlots("saint-rambert")}
                        >
                          <MapPin className="w-5 h-5 text-green-500" />
                          <span>Saint-Rambert</span>
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Loading */}
                  {loadingSlots && (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                      <p className="text-slate-500">Chargement des créneaux...</p>
                    </div>
                  )}
                  
                  {/* Slots list */}
                  {selectedZone && !loadingSlots && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-slate-700">
                          Créneaux disponibles ({selectedZone === "lyon" ? "Lyon" : "Saint-Rambert"})
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedZone(null); setAvailableSlots([]); }}
                        >
                          Changer de zone
                        </Button>
                      </div>
                      
                      {availableSlots.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          Aucun créneau disponible dans les 30 prochains jours
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {availableSlots.map(slot => (
                            <div
                              key={slot.id}
                              className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                            >
                              <div>
                                <p className="font-medium">
                                  {format(new Date(slot.date), "EEEE d MMMM", { locale: fr })}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {slot.start_time} - {slot.end_time}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {slot.max_bookings - slot.current_bookings} place(s)
                                </Badge>
                                <Button
                                  size="sm"
                                  disabled={bookingSlot}
                                  onClick={() => bookDeliverySlot(slot)}
                                  className="bg-blue-500 hover:bg-blue-600"
                                >
                                  {bookingSlot ? "..." : "Réserver"}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
                <div className="border-t p-4">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setShowSlotModal(false); setSelectedZone(null); setAvailableSlots([]); }}
                  >
                    Fermer
                  </Button>
                </div>
              </Card>
            </div>
          )}
          
          {/* Articles by category */}
          {Object.entries(ARTICLES).map(([key, category]) => (
            <CategorySection
              key={key}
              category={category}
              quantities={quantities}
              onQuantityChange={handleQuantityChange}
              searchTerm={searchTerm}
            />
          ))}
          
          {/* Remarks */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="w-5 h-5 text-pink-500" />
                Remarques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Taches, défauts, instructions particulières..."
                rows={4}
                className="text-base"
              />
            </CardContent>
          </Card>
          
          {/* Submit Button */}
          <div className="sticky bottom-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                onClick={handlePrintClick}
                disabled={getTotalArticles() === 0}
                variant="outline"
                className="h-14 text-lg border-2 border-slate-300 hover:bg-slate-100"
              >
                <span className="flex items-center gap-2">
                  <Printer className="w-5 h-5" />
                  Imprimer ticket
                </span>
              </Button>
              <Button
                type="submit"
                disabled={sending || getTotalArticles() === 0 || !customer?.email}
                className="h-14 text-lg bg-pink-500 hover:bg-pink-600 shadow-lg"
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Envoi...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Envoyer email
                  </span>
                )}
              </Button>
            </div>
            <Button
              type="button"
              onClick={() => saveDeposit(false)}
              disabled={sending || getTotalArticles() === 0}
              className="w-full h-14 text-lg bg-green-600 hover:bg-green-700 shadow-lg"
              data-testid="deposit-save-btn"
            >
              <span className="flex items-center gap-2">
                <Save className="w-5 h-5" />
                Enregistrer (sans email)
              </span>
            </Button>
            {!customer?.email && getTotalArticles() > 0 && (
              <p className="text-center text-sm text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Ce client n'a pas d'email - Utilisez "Imprimer ticket" ou "Enregistrer"
              </p>
            )}
          </div>
        </form>
      </main>
      
      {savedDeposit && (
        <DepositSavedModal
          deposit={savedDeposit}
          phone={customer?.phone}
          initialMessage={buildDepositSms(customer?.first_name || "", ticketNumber, getTotalArticles(), estimatedDate)}
          getAuth={getAuth}
          onClose={() => navigate("/admin/customers")}
        />
      )}
      
      {/* Print Amount Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-xl font-bold text-slate-800 mb-4 text-center">
              🖨️ Impression du ticket
            </h3>
            <div className="mb-6">
              <Label className="text-base font-medium mb-2 block">
                Montant total (€)
              </Label>
              <Input
                type="number"
                step="0.01"
                value={printAmount}
                onChange={(e) => setPrintAmount(e.target.value)}
                placeholder="Ex: 45.50"
                className="h-14 text-2xl text-center font-bold"
                autoFocus
              />
              <p className="text-sm text-slate-500 mt-2 text-center">
                Laissez vide si vous ne souhaitez pas afficher le montant
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPrintModal(false)}
                className="h-12"
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handlePrint}
                className="h-12 bg-pink-500 hover:bg-pink-600"
              >
                <Printer className="w-5 h-5 mr-2" />
                Imprimer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
